import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Share2, Copy, Check, MessageCircle, Send,
  Facebook as FacebookIcon, Twitter as TwitterIcon, Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const formatCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}K`;
  return String(v);
};

/* ------------------------------------------------------------------ */
/* Follow button                                                       */
/* ------------------------------------------------------------------ */
export const FollowButton = ({ artistId, isFollowing, followerCount, onChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState({ following: !!isFollowing, count: Number(followerCount) || 0 });

  React.useEffect(() => {
    setLocal({ following: !!isFollowing, count: Number(followerCount) || 0 });
  }, [isFollowing, followerCount]);

  const handleClick = useCallback(async () => {
    if (!user) {
      navigate('/login', { state: { redirectTo: window.location.pathname } });
      return;
    }
    if (busy) return;
    setBusy(true);
    const next = !local.following;
    // Optimistic UI
    setLocal((p) => ({ following: next, count: Math.max(0, p.count + (next ? 1 : -1)) }));
    try {
      const url = `/artists/${artistId}/follow`;
      const res = next ? await api.post(url) : await api.delete(url);
      const fresh = { following: !!res.data?.following, count: Number(res.data?.follower_count) || 0 };
      setLocal(fresh);
      onChange?.(fresh);
      toast.success(next ? 'Following! You will see their updates.' : 'Unfollowed.');
    } catch (e) {
      // Revert
      setLocal((p) => ({ following: !next, count: Math.max(0, p.count + (next ? -1 : 1)) }));
      toast.error('Could not update follow. Please try again.');
    } finally { setBusy(false); }
  }, [user, navigate, busy, local.following, artistId, onChange]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid="artist-follow-button"
      aria-pressed={local.following}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-all duration-200 ${
        local.following
          ? 'glass-strong border-fuchsia-400/40 text-white'
          : 'border-white/15 text-white hover:border-fuchsia-400/40'
      } ${busy ? 'opacity-60 cursor-wait' : ''}`}
    >
      <Heart
        className={`h-4 w-4 transition-all ${local.following ? 'fill-fuchsia-400 text-fuchsia-400' : ''}`}
        strokeWidth={local.following ? 2 : 2.2}
      />
      {local.following ? 'Following' : 'Follow'}
      <span className="text-xs text-slate-400 font-mono">· {formatCount(local.count)}</span>
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Share button + sheet                                                */
/* ------------------------------------------------------------------ */
export const ShareButton = ({ artist, compact = false }) => {
  const [open, setOpen] = useState(false);
  const profileUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const username = artist?.username;
    const base = window.location.origin;
    return username ? `${base}/u/${username}` : `${base}/artists/${artist?.artist_id || ''}`;
  }, [artist]);

  const message = useMemo(() => {
    const name = artist?.stage_name || 'this artist';
    const cats = (artist?.categories || []).slice(0, 2).join(' · ');
    return cats
      ? `Check out ${name} on Maarivil \u2014 ${cats}.`
      : `Check out ${name} on Maarivil.`;
  }, [artist]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success('Profile link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const onNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: artist?.stage_name, text: message, url: profileUrl });
        setOpen(false);
        return true;
      } catch { /* user cancelled */ }
    }
    return false;
  };

  const handleClick = async () => {
    // Try the native share sheet first (iOS/Android) — falls back to our modal.
    const used = await onNativeShare();
    if (!used) setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="artist-share-button"
        className={`inline-flex items-center gap-2 rounded-full border border-white/15 text-white hover:border-cyan-400/40 transition-all duration-200 ${
          compact ? 'px-4 py-2 text-sm' : 'px-5 py-2.5 text-sm'
        }`}
      >
        <Share2 className="h-4 w-4" /> Share
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="artist-share-sheet"
        >
          <div
            className="w-full max-w-md glass-strong rounded-2xl border border-white/10 p-6 space-y-4 animate-in slide-in-from-bottom-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="font-display text-xl text-white">Share this artist</h3>
      <p className="text-xs text-slate-400 mt-1">Spread the word — every share grows their reach.</p>
            </div>
            <div className="rounded-xl glass border border-white/10 px-3 py-2.5 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-200 truncate flex-1" data-testid="artist-share-url">{profileUrl}</span>
              <button
                type="button"
                onClick={onCopy}
                data-testid="artist-share-copy-button"
                className="text-slate-300 hover:text-white text-xs px-2 py-1 rounded-md border border-white/10 inline-flex items-center gap-1"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3 pt-1">
              <ShareTile
                label="WhatsApp"
                color="from-emerald-500 to-emerald-700"
                icon={MessageCircle}
                href={`https://wa.me/?text=${encodeURIComponent(`${message} ${profileUrl}`)}`}
                testId="artist-share-whatsapp"
              />
              <ShareTile
                label="Telegram"
                color="from-sky-500 to-sky-700"
                icon={Send}
                href={`https://t.me/share/url?url=${encodeURIComponent(profileUrl)}&text=${encodeURIComponent(message)}`}
                testId="artist-share-telegram"
              />
              <ShareTile
                label="X"
                color="from-slate-700 to-slate-900"
                icon={TwitterIcon}
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(profileUrl)}&text=${encodeURIComponent(message)}`}
                testId="artist-share-twitter"
              />
              <ShareTile
                label="Facebook"
                color="from-blue-600 to-blue-800"
                icon={FacebookIcon}
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`}
                testId="artist-share-facebook"
              />
            </div>
            <p className="text-[11px] text-slate-500 text-center pt-1">
              Want to put this in your Instagram bio? Paste <span className="text-white">{profileUrl}</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const ShareTile = ({ label, color, icon: Icon, href, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="flex flex-col items-center gap-1.5 group"
  >
    <span
      className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform`}
    >
      <Icon className="h-5 w-5 text-white" />
    </span>
    <span className="text-[10px] uppercase tracking-wider text-slate-300">{label}</span>
  </a>
);

/* ------------------------------------------------------------------ */
/* Confirmation icon — exported for reuse                              */
/* ------------------------------------------------------------------ */
export const CopiedTick = () => <Check className="h-4 w-4 text-emerald-400" />;
